import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { SEVERITY, blockingAlerts, warningAlerts, infoAlerts } from './rightsAlerts';

const { FiAlertTriangle, FiAlertCircle, FiInfo, FiChevronDown, FiChevronUp, FiCheckCircle } = FiIcons;

const ICON_FOR = {
  block: FiAlertTriangle,
  warn: FiAlertCircle,
  info: FiInfo,
};

const AlertRow = ({ alert, onSelect }) => {
  const severity = SEVERITY[alert.severity];
  const Icon = ICON_FOR[alert.severity];
  return (
    <div className={`rounded-lg border ${severity.border} ${severity.bg} p-3`}>
      <div className="flex items-start gap-2">
        <SafeIcon icon={Icon} className={`w-4 h-4 ${severity.text} shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-bold ${severity.text}`}>{alert.title}</p>
            <span className="text-[10px] uppercase tracking-widest text-text-muted">{alert.itemTitle}</span>
          </div>
          <p className="text-xs text-text-main mt-1">{alert.detail}</p>
          {alert.action && <p className="text-[11px] text-text-muted italic mt-1">{alert.action}</p>}
        </div>
        {onSelect && alert.itemId && (
          <button type="button" onClick={() => onSelect(alert.itemId)}
            className="text-[11px] font-bold text-accent-gold underline shrink-0">
            View
          </button>
        )}
      </div>
    </div>
  );
};

const RightsAlertsPanel = ({ alerts, onSelectItem, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const blocks = blockingAlerts(alerts);
  const warnings = warningAlerts(alerts);
  const infos = infoAlerts(alerts);
  const total = alerts.length;

  if (total === 0) {
    return (
      <div className="bg-white border border-brand-dark rounded-2xl p-4 mb-4 flex items-center gap-2">
        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
        <p className="text-sm font-semibold text-green-700">No rights alerts were returned by the programme API.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-brand-dark rounded-2xl mb-4 overflow-hidden">
      <button type="button" onClick={() => setOpen(value => !value)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-brand-light/50 transition-colors">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiAlertTriangle} className={`w-5 h-5 ${blocks.length ? 'text-red-600' : 'text-amber-600'}`} />
          <span className="font-serif text-sm font-bold text-accent-navy">Rights alerts</span>
          <div className="flex items-center gap-1.5">
            {blocks.length > 0 && <span className="text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 rounded-full px-2 py-0.5">{blocks.length} blocking</span>}
            {warnings.length > 0 && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{warnings.length} warning{warnings.length > 1 ? 's' : ''}</span>}
            {infos.length > 0 && <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">{infos.length} info</span>}
          </div>
        </div>
        <SafeIcon icon={open ? FiChevronUp : FiChevronDown} className="w-4 h-4 text-text-muted" />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {blocks.length > 0 && <p className="text-[11px] font-bold uppercase tracking-widest text-red-600 pt-1">Must fix before submitting</p>}
          {blocks.map((alert, index) => <AlertRow key={`b${index}`} alert={alert} onSelect={onSelectItem} />)}
          {warnings.length > 0 && <p className="text-[11px] font-bold uppercase tracking-widest text-amber-600 pt-2">Should review</p>}
          {warnings.map((alert, index) => <AlertRow key={`w${index}`} alert={alert} onSelect={onSelectItem} />)}
          {infos.length > 0 && <p className="text-[11px] font-bold uppercase tracking-widest text-blue-600 pt-2">For your information</p>}
          {infos.map((alert, index) => <AlertRow key={`i${index}`} alert={alert} onSelect={onSelectItem} />)}
        </div>
      )}
    </div>
  );
};

export default RightsAlertsPanel;